/**
 * RepairQuoteDeviceDetails — read-only device card for a repair quote.
 *
 * Resolves the device live from the linked work item's check-in (no
 * snapshot on the quote). When the linked check-in has more than one
 * device, surfaces a Select so the tech can pick which device the
 * quote covers; the selection is persisted on the quote via
 * `onChangeSourceCheckInDraftJobId`.
 */

import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LaptopIcon from '@mui/icons-material/Laptop';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import TabletMacIcon from '@mui/icons-material/TabletMac';
import TvIcon from '@mui/icons-material/Tv';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import StorageIcon from '@mui/icons-material/Storage';
import UsbIcon from '@mui/icons-material/Usb';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import type {
  CheckInDeviceType,
  CheckInDraftJob,
} from '@techgitter/contracts';

import { useQuoteSourceDevice } from '../../hooks/useQuoteSourceDevice';
import {
  INSPECTION_SCHEMA,
  inspectionToDraft,
} from '../../utils/inspectionSchema';
import { checkInDeviceLabel } from '@techgitter/contracts';

export interface RepairQuoteDeviceDetailsProps {
  sm4rtJobId: string | null;
  sourceCheckInDraftJobId: string | null;
  onChangeSourceCheckInDraftJobId: (id: string | null) => void;
  disabled?: boolean;
}


const DEVICE_ICON: Record<CheckInDeviceType, React.ReactNode> = {
  laptop: <LaptopIcon fontSize="small" />,
  desktop: <DesktopWindowsIcon fontSize="small" />,
  all_in_one: <TvIcon fontSize="small" />,
  phone: <SmartphoneIcon fontSize="small" />,
  tablet: <TabletMacIcon fontSize="small" />,
  hard_drive: <StorageIcon fontSize="small" />,
  usb_flash_drive: <UsbIcon fontSize="small" />,
  console: <SportsEsportsIcon fontSize="small" />,
  other: <DevicesOtherIcon fontSize="small" />,
};

const DeviceIcon: React.FC<{ deviceType: CheckInDeviceType }> = ({
  deviceType,
}) => <>{DEVICE_ICON[deviceType]}</>;

export const RepairQuoteDeviceDetails: React.FC<
  RepairQuoteDeviceDetailsProps
> = ({
  sm4rtJobId,
  sourceCheckInDraftJobId,
  onChangeSourceCheckInDraftJobId,
  disabled = false,
}) => {
  const { availableDevices, device, isLoading, error, workItem } =
    useQuoteSourceDevice({ sm4rtJobId, sourceCheckInDraftJobId });

  // No linkage at all — hide the block entirely.
  if (!sm4rtJobId) return null;

  // We need the work item to know whether a check-in exists.
  // While the work item is loading we show a thin shell so the page
  // doesn't shift; once we know there's no check-in we return null.
  if (isLoading && !workItem) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading device details…
          </Typography>
        </Stack>
      </Paper>
    );
  }

  // No check-in attached to the source work item — nothing to show.
  if (!workItem?.checkInSessionGuid) return null;

  if (error) {
    return (
      <Alert severity="warning">
        Couldn’t load device details: {error}
      </Alert>
    );
  }

  if (availableDevices.length === 0) {
    return (
      <Alert severity="info" variant="outlined">
        The linked check-in has no devices recorded.
      </Alert>
    );
  }

  // Multi-device, nothing picked yet — render the picker only.
  if (!device && availableDevices.length > 1) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1}>
          <Typography variant="subtitle2">Device</Typography>
          <Typography variant="caption" color="text.secondary">
            The linked check-in has {availableDevices.length} devices. Pick
            which one this quote covers.
          </Typography>
          <TextField
            select
            size="small"
            label="Device on this quote"
            value={sourceCheckInDraftJobId ?? ''}
            onChange={(e) =>
              onChangeSourceCheckInDraftJobId(e.target.value || null)
            }
            disabled={disabled}
          >
            {availableDevices.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {formatDeviceMenuLabel(d)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>
    );
  }

  // Saved selection no longer matches any available device (e.g. the
  // draft job was deleted). Treat as "no selection" but warn so the
  // tech knows why nothing is showing.
  if (!device && sourceCheckInDraftJobId) {
    return (
      <Alert severity="warning">
        The device this quote was linked to is no longer on the check-in.
        Pick another device above or clear the link.
      </Alert>
    );
  }

  // Single-device case — `device` resolved off the only available
  // entry; render the details directly. Multi-device with a selection
  // — same render plus a "Change device" link.
  if (!device) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <DeviceIcon deviceType={device.deviceType} />
          <Typography variant="subtitle2">Device</Typography>
          <Chip
            size="small"
            label={checkInDeviceLabel(device)}
            variant="outlined"
          />
          {device.inspection?.brandNewInBox && (
            <Chip
              size="small"
              color="info"
              variant="outlined"
              label="Brand new, in unopened box"
            />
          )}
          <Box sx={{ flex: 1 }} />
          {availableDevices.length > 1 && (
            <Button
              size="small"
              variant="text"
              disabled={disabled}
              onClick={() => onChangeSourceCheckInDraftJobId(null)}
              sx={{ textTransform: 'none', minWidth: 0 }}
            >
              Change device
            </Button>
          )}
        </Stack>

        <DeviceIdentifyGrid device={device} />

        <DamageItemSection device={device} />

        {device.inspection?.otherNotes && (
          <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Other notes
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {device.inspection.otherNotes}
            </Typography>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDeviceMenuLabel(d: CheckInDraftJob): string {
  const parts: string[] = [];
  parts.push(`#${d.displayIndex} · ${checkInDeviceLabel(d)}`);
  const insp = d.inspection;
  if (insp?.manufacturer) parts.push(insp.manufacturer);
  if (insp?.serialNumber) parts.push(`SN ${insp.serialNumber}`);
  if (d.serviceSummary) parts.push(d.serviceSummary);
  return parts.join(' — ');
}

const DeviceIdentifyGrid: React.FC<{ device: CheckInDraftJob }> = ({
  device,
}) => {
  const insp = device.inspection;
  const manufacturer = insp?.manufacturer ?? '—';
  const serial = (() => {
    if (insp && 'serialNotLegible' in insp && insp.serialNotLegible) {
      return insp.serialNumber
        ? `${insp.serialNumber} (not legible)`
        : 'Not legible';
    }
    return insp?.serialNumber || '—';
  })();
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        gap: 1,
      }}
    >
      <DefRow label="Manufacturer" value={manufacturer} />
      <DefRow label="Serial" value={serial} />
      {device.serviceSummary && (
        <DefRow label="Service" value={device.serviceSummary} />
      )}
    </Box>
  );
};

const DefRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 1 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
);

const DamageItemSection: React.FC<{ device: CheckInDraftJob }> = ({
  device,
}) => {
  const insp = device.inspection;
  if (!insp) return null;
  // Brand-new-in-box devices skip physical inspection entirely.
  if (insp.brandNewInBox) return null;
  const schema = INSPECTION_SCHEMA[device.deviceType];
  // Use the existing draft mapper — it flattens the per-device-type
  // inspection shape (e.g. AllInOne stores screenDamage under .checks)
  // into a uniform damageChecks / itemStatusChecks pair that mirrors
  // the schema's row layout.
  const draft = inspectionToDraft(device.deviceType, insp);

  interface Notable {
    label: string;
    notes: string | null;
  }

  // Damage rows: `yes` means damage was observed.
  const damageNotables: Notable[] = schema.damageRows
    .map((row) => {
      const c = draft.damageChecks[row.key];
      if (c?.status === 'yes') return { label: row.label, notes: c.notes };
      return null;
    })
    .filter((n): n is Notable => n !== null);

  // Item-status rows: `no` means there's a problem (e.g. "all feet
  // present? no" → missing feet).
  const itemStatusNotables: Notable[] = schema.itemStatusRows
    .map((row) => {
      const c = draft.itemStatusChecks[row.key];
      if (c?.status === 'no') return { label: row.label, notes: c.notes };
      return null;
    })
    .filter((n): n is Notable => n !== null);

  if (damageNotables.length === 0 && itemStatusNotables.length === 0) {
    return (
      <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Inspection
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No damage or item-status issues recorded.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1 }}>
      <Typography variant="caption" color="text.secondary">
        Inspection findings
      </Typography>
      <Stack spacing={0.25} sx={{ mt: 0.5 }}>
        {damageNotables.map((row) => (
          <NotableRow
            key={`d-${row.label}`}
            label={row.label}
            statusLabel="Damaged"
            notes={row.notes}
          />
        ))}
        {itemStatusNotables.map((row) => (
          <NotableRow
            key={`i-${row.label}`}
            label={row.label}
            statusLabel="Issue"
            notes={row.notes}
          />
        ))}
      </Stack>
    </Box>
  );
};

const NotableRow: React.FC<{
  label: string;
  statusLabel: string;
  notes: string | null;
}> = ({ label, statusLabel, notes }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 1 }}>
    <Box>
      <Typography variant="body2">{label}</Typography>
      {notes && (
        <Typography variant="caption" color="text.secondary">
          {notes}
        </Typography>
      )}
    </Box>
    <Chip
      label={statusLabel}
      size="small"
      color="warning"
      variant="filled"
      sx={{ height: 22, fontSize: 11 }}
    />
  </Box>
);
